import { getProfile, getSession, getSupabase, updateMyProfile } from './auth';
import { DEFAULT_PRICE_CENTS, formatCents } from './pricing';

interface Creator {
  id: string; username: string; display_name: string; avatar_url: string | null; banner_url: string | null;
  bio: string; verified: boolean; prices: typeof DEFAULT_PRICE_CENTS; tips_enabled?: boolean;
}
interface CatalogVideo {
  id: string; creator_id: string; creator: Creator; title: string; category: string; duration: string | null;
  duration_seconds?: number; view_count: number; created_at: string; members_only: boolean; minimum_tier: string;
  locked: boolean; description: string; tags: string[]; thumbnail_url: string | null;
}
interface Catalog { creators: Creator[]; videos: CatalogVideo[] }
interface ChannelPage {
  creator: { id: string; username: string; displayName: string; avatarUrl: string | null; bannerUrl: string | null; bio: string; verified: boolean; membersOnlyPage: boolean; tipsEnabled?: boolean };
  prices: typeof DEFAULT_PRICE_CENTS;
  videos: { id: string; title: string; category: string; duration: string | null; views: number; createdAt: string; description: string | null; tags: string[]; locked: boolean; requires: string | null; thumbnailUrl: string | null }[];
}

function durationSeconds(value: string | null): number {
  if (!value) return 0;
  const parts = value.split(':').map(Number);
  return parts.every(Number.isFinite) ? parts.reduce((sum, part) => sum * 60 + part, 0) : 0;
}

export async function load(route: string, signal: AbortSignal): Promise<Catalog> {
  if (route !== '/' && !route.startsWith('/watch/') && !route.startsWith('/creator/')) return { creators: [], videos: [] };
  const session = await getSession();
  const headers: HeadersInit = session ? { Authorization: `Bearer ${session.access_token}` } : {};
  async function read<T>(url: string): Promise<T> {
    const response = await fetch(url, { headers, signal, cache: 'no-store' });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Couldn’t load content. Please try again.');
    return data;
  }
  let catalog: Catalog;
  if (route.startsWith('/creator/')) {
    const page = await read<ChannelPage>(`/api/creators/${encodeURIComponent(route.slice(9))}/page`);
    const creator: Creator = {
      id: page.creator.id, username: page.creator.username, display_name: page.creator.displayName,
      avatar_url: page.creator.avatarUrl, banner_url: page.creator.bannerUrl, bio: page.creator.bio || '',
      verified: page.creator.verified, prices: page.prices, tips_enabled: page.creator.tipsEnabled !== false,
    };
    catalog = { creators: [creator], videos: page.videos.map(v => ({
      id: v.id, creator_id: creator.id, creator, title: v.title, category: v.category, duration: v.duration,
      view_count: v.views, created_at: v.createdAt, members_only: Boolean(v.requires), minimum_tier: v.requires || 'supporter',
      locked: v.locked, description: v.description || '', tags: v.tags, thumbnail_url: v.thumbnailUrl,
    })) };
  } else {
    if (route.startsWith('/watch/')) {
      const [feed, detail] = await Promise.all([
        read<Catalog>('/api/catalog'),
        read<Catalog>(`/api/catalog/videos/${encodeURIComponent(route.slice(7))}`),
      ]);
      catalog = {
        creators: [...new Map([...feed.creators, ...detail.creators].map(c => [c.id, c])).values()],
        videos: [...new Map([...feed.videos, ...detail.videos].map(v => [v.id, v])).values()],
      };
    } else catalog = await read<Catalog>('/api/catalog');
  }
  return { creators: catalog.creators, videos: catalog.videos.map(v => ({ ...v, duration_seconds: durationSeconds(v.duration) })) };
}

export function price(creator: Creator | null, tier: keyof typeof DEFAULT_PRICE_CENTS): string {
  return formatCents(creator?.prices?.[tier] ?? DEFAULT_PRICE_CENTS[tier]);
}

const defaults = { mature_content: true, email_notifications: false, creator_notifications: true };
function preferences() {
  try { return { ...defaults, ...JSON.parse(localStorage.getItem('ah-preferences') || '{}') }; } catch { return defaults; }
}

export async function account() {
  const session = await getSession();
  if (!session) return { profile: null, settings: preferences(), subscriptions: [] };
  const profile = await getProfile(session.user.id);
  const { data, error } = await getSupabase()!.from('memberships')
    .select('id, tier, status, expires_at, creator:profiles!memberships_creator_id_fkey(display_name, username)')
    .eq('user_id', session.user.id);
  if (error) throw error;
  return { profile, settings: preferences(), subscriptions: (data || []).map(m => ({ ...m, current_period_end: m.expires_at })) };
}

export async function saveAccount(profile: { display_name: string; username: string; bio: string }, settings: typeof defaults) {
  await updateMyProfile(profile);
  localStorage.setItem('ah-preferences', JSON.stringify(settings));
}
